import { BoardRowType, Task } from '@common-models/task';
import { TasksGroup } from '@common-models/task-group';
import { getSelectedBoardId } from '@common-reducers/BoardsSelectors';
import { getBulkTaskList } from '@common-reducers/BulkActionsReducer';
import { DBTasksThunks, taskgroupsSelectors, tasksSelectByQuery } from "@common-reducers/DBServiceThunks";
import { taskActions } from '@common-reducers/TasksReducer';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { namespace } from '@common/config/constants';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

type Props = {
    taskId: string
};

export default function SelectParentTask({ taskId }: Props) {
    const { t } = useTranslation(namespace);
    const boardId = useAppSelector((state) => getSelectedBoardId(state));
    const [taskFilterText, setTaskFilterText] = useState<string>('');
    const bulkTasksList = useAppSelector(getBulkTaskList);
    const dispatch = useAppDispatch();
    const activeTaskid = useAppSelector((state) => state.TasksReducer.menuActiveTaskId);
    const move_to = useAppSelector((state) => state.BulkActionsReducer.move_to);

    const tasksFilter: any = {
        boardId,
        text: new RegExp(taskFilterText, "i"),
        $or: [{ deleted: { $exists: false } }, { deleted: false }]
    };

    const groups: TasksGroup[] = useAppSelector(taskgroupsSelectors.selectAll);

    // Using existing selector logic
    const tasksList: Task[] = useAppSelector((state) => tasksSelectByQuery(state.DBTasksReducer,
        tasksFilter,
        [
            "_id",
            "groupId",
            "order",
            "text",
            "showSubTasks",
            "showAllSubTasks",
            "parentTaskId",
            "priority",
            "priorityId",
            "status",
            "statusId",
            "owner",
            "person",
            "taskType",
            "createdAt",
            "customData",
            "updatedAt",
        ],
    ));

    // convert task subTask 
    function convertTaskToSub(parentTask: Task) {
        // The logic from original file
        // const task = tasksList.find((task) => task._id === activeTaskid); // Unused in original logic
        let taskType: string = BoardRowType.subTask;
        let tasksToPatch = bulkTasksList;
        if (tasksToPatch.length === 0){
             // If bulk list empty, assume we operate on the active (current) task?
             // But usually activeTaskId is the one we clicked menu on.
             // Original logic used `activeTaskid` here if bulk list was empty.
             // Note: `taskId` prop is passed to this component, which should match activeTaskid in context of the menu.
            tasksToPatch = [{_id: activeTaskid || taskId}]; 
        }
  
        const changes = tasksToPatch.map((task) => {
            let change = {
                id: task._id,
                changes: {
                    groupId: parentTask.groupId,
                    parentTaskId: parentTask._id,
                    taskType
                },
            };
            return change;
        });

        dispatch(DBTasksThunks.patchMany(changes));
        dispatch(taskActions.toggleTaskMenu(''));
    }

    function parentTasksList(groupId: string) {
        return tasksList.filter((task) => task.groupId === groupId && task.taskType !== 'subTask');
    }

    return (
        <div className="flex flex-col gap-2 p-1 min-w-[220px]">
            <h4 className="text-sm font-medium leading-none mb-1">
                {t('selectParentTask.chooseParentTask')}
            </h4>
            
            <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input 
                    placeholder={`${t('selectParentTask.searchTaskPlaceholder')}`} 
                    className="pl-8 h-9"
                    value={taskFilterText}
                    onChange={(e) => setTaskFilterText(e.target.value)}
                    // Stop propagation to prevent menu closing when typing space
                    onKeyDown={(e) => e.stopPropagation()} 
                />
            </div>

            <div className="max-h-[200px] overflow-y-auto mt-1 pr-1 custom-scrollbar">
                {groups.map((group) => {
                    const tasksInGroup = parentTasksList(group._id ?? "");
                    if (tasksInGroup.length === 0) return null;

                    return (
                        <div key={group._id} className="mb-2">
                             {/* Group Header */}
                            <div className="flex items-center gap-2 px-2 py-1 mb-1">
                                <div 
                                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: group.colorAccent ?? '#00c875' }}
                                />
                                <span className="text-xs font-semibold text-muted-foreground truncate">
                                    {group.name}
                                </span>
                            </div>

                            {/* Tasks List */}
                            <div className="flex flex-col gap-0.5">
                                {tasksInGroup.map((taskItem) => {
                                    if (taskItem._id === taskId) return null; // Don't allow parenting to self

                                    return (
                                        <button
                                            key={taskItem._id}
                                            onClick={() => convertTaskToSub(taskItem)}
                                            className={cn(
                                                "w-full text-left text-sm px-2 py-1.5 rounded-sm transition-colors",
                                                "hover:bg-accent hover:text-accent-foreground",
                                                "truncate"
                                            )}
                                        >
                                            {taskItem.text}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
                {/* Empty state if nothing found? */}
                {tasksList.length === 0 && (
                     <div className="py-2 text-center text-xs text-muted-foreground">
                        {t('tasks.noTasksFound', 'No tasks found')}
                    </div>
                )}
            </div>
        </div>
    );
}